import Vue from 'vue';
import axios from 'axios';
import toastr from 'toastr';
import 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only';
import { i18n } from '@/plugins/i18n';
import ErrorCode from '@/utils/classes/ErrorCode';
import { calert } from '@/utils/main';
import { router } from '@/plugins/router';
import SPage from '@/utils/classes/SPage';
import { globalEventDispatcher, ShizenGlobalEvent } from '@/utils/event-dispatcher/global';
import store from '../store';


/**
 * This is an array of regex to match the sent URL against.
 * Shizen is currently transitioning to a better Laravel API.
 * If the sent URL matches one of this array's, the URL will be left unchanged.
 * If it doesn't but matches /server/*.php, it will be redirected to legacy
 * @type {Array}
 */
const migratedUrls = [
  /actions\/filter/,
  /admin\/users/,
  /auth\/[a-z]+/,
  /file\/(download\/)?\d+/,
  /file\/upload/,
  /gembawalks\/\d+\/steps\/\d+\/problems\/\d+/,
  /notifications/,
  /problem\/types/,
  /problems$/,
  /problems\/export/,
  /problems\/filter/,
  /problems\/\d+/,
  /problems\/\d+\/notifications/,
  /problems\/\d+\/subscribe/,
  /problems\/\d+\/unsubscribe/,
  /ratings/,
  /server\/locations/,
  /team\/parameters/,
  /teams\/\d+\/users/,
  /users\/export/,
  /users\/\d+/,
  /teams\/\d+\/users/,
  /teams\/users/,
];

// Send default params (eg: Shizen version)
export const serverAPI = axios.create({
  baseURL: '',
});

let xhrUniqueID = 1;

// Set config
serverAPI.interceptors.request.use((config) => {
  config.xhrUniqueID = ++xhrUniqueID;
  // Default params need to be set here to not be completely overwrite
  const defaultParams = {
    slean_version: process.env.VUE_APP_VERSION,
    timezone: Vue.moment.tz.guess(),
    locale: i18n.t('min'),
  };
  const defaultData = {};

  config.headers.Authorization = `Bearer ${store.getters.currentJwt}`;
  // config default boolean param
  if (config.url && config.url.includes('server/config/')) {
    defaultParams.config = true;
  }

  if (SPage.configPageLinks.includes(router.currentRoute.path)) {
    defaultData.config = true;
  }

  config.params = Object.assign(defaultParams, config.params);
  if (!(config.data instanceof FormData)) {
    config.data = Object.assign(defaultData, config.data);
  }

  // Update URL to legacy while the new API isn't setup
  if (!migratedUrls.some(rx => rx.test(config.url))) {
    config.url = config.url.replace(/\/server\/(.+)/, '/legacy/$1');
  }

  // Allow cancellation from the newtork manager
  if (!config.controller) {
    config.controller = new AbortController();
    config.signal = config.controller.signal;
  }
  // Emit the event to the network manager
  globalEventDispatcher.dispatch(ShizenGlobalEvent.XHR_STARTED, { config });
  // Output the XHR config, or cancel it if offlineMode is enabled
  if (store.state.device.offlineMode && config.syncing === undefined) {
    config.controller.abort();
  }

  return config;
});

// Intercept all responses
serverAPI.interceptors.response.use(
  (response) => {
    globalEventDispatcher.dispatch(ShizenGlobalEvent.XHR_ENDED, response);
    return response;
  },
  (error) => {
    // TODO: Prompt the user with offline mode when response isn't present
    globalEventDispatcher.dispatch(ShizenGlobalEvent.XHR_ENDED, error);
    if (!error.response) {
      return Promise.reject(error);
    }
    const { data } = error.response;
    if (data === ErrorCode.WRONG_VERSION) {
      // Client version is outdated
      calert(i18n.t('message.NEW_VERSION_RELOAD'), [
        {
          title: i18n.t('message.REFRESH'),
          action: () => {
            location.reload();
          },
        },
      ]);
    } else if (data === ErrorCode.ACCOUNT_LOCKED) {
      // Account is locked
      calert(i18n.t('message.ACCOUNT_LOCKED'), [
        {
          title: i18n.t('message.OK'),
          action: () => {
            router.push('/login');
          },
        },
      ]);
    } else if (data === ErrorCode.COVID_RESTRICTED) {
      // Free 'covid' version, feature not accessible
      const restrictedTxt = i18n.t('message.FREE_VERSION_RESTRICTED') + i18n.t('message.GET_FULL_SHIZEN_LINK');
      toastr.remove();
      toastr.info(restrictedTxt, '', { timeOut: 8000, preventDuplicates: false });
    } else if (data instanceof Object && data.code !== undefined) {
      data.code = Number(data.code);
      if (error.response.status === 401 && data.code === ErrorCode.UNAUTHORIZED) {
        toastr.error(i18n.t('message.ERROR_UNAUTHORIZE'));
      }
    }
    return Promise.reject(error);
  },
);

export default {
  serverAPI,
};
